// ── ARC INDIVIDUAL COVERAGE — the arithmetic, and what it refuses to guess ───
//
// Kevin, 2026-10-07, after the 2026 signup opened: *"figure out how to build this into an
// ARC/PLC decision tool. We have a lot of the framework built, but we didnt account for
// ARC IC."* And on scope: *"a screener by farm ... Something where we can pull up a farm
// and run it to see how it looks. A customized run."*
//
// ⚠ THIS IS A SCREENER, NOT A CALCULATOR, AND THE DIFFERENCE IS WHAT IT MAY CLAIM.
// It answers "is ARC-IC worth taking to the FSA office for this farm before December 11",
// on inputs that are partly ESTIMATED. It does not compute a payment anybody can rely on,
// and every surface that renders it says which half is which.
//
// ── WHY THIS YEAR AND NOT ANY OTHER ─────────────────────────────────────────
// The 2026 signup runs 16 Sep – 11 Dec 2026, so for the first time the election is made
// AFTER harvest: a grower knows their actual yield before they sign. The 2027 signup
// (2 Nov 2026 – 15 Mar 2027) is back to a forecast. That is the whole reason this is worth
// building now and why it is a one-year window.
//
// ── WHAT IS SOURCED, AND FROM WHERE ─────────────────────────────────────────
// The PRICE side is already in this database, pulled from FSA by tools/arc-plc/ingest.mjs
// into `arc_plc_reference`. CY2026, read 2026-10-07:
//
//     crop        ARC benchmark price    MYA (forecast)
//     CORN               $5.03               $4.80
//     SOYBEANS          $12.17              $12.00
//     WHEAT              $6.98               $6.40
//
// Those are FSA's own figures and they are what the arithmetic below multiplies. They are
// NOT typed here — the caller passes them from `arc_plc_reference`, so a republished MYA
// moves this tool without a code change. The constants below are the STATUTORY parameters,
// which do not move with a price.
//
// ── THE GUARANTEE AND THE CAP BOTH MOVED, AND BOTH ARE OBBBA ────────────────
// ARC-IC pays when actual revenue falls below 90% of benchmark revenue (it was 86%), and
// the payment is capped at 12% of benchmark revenue (it was 10%). ⚠ The 12% is the same cap
// `arcPlcRule.js` already carries and that file records that FSA's own fact sheet says 10%
// and is WRONG — do not "correct" either of them back.
export const ARC_IC_GUARANTEE_FRAC = 0.90;

// ⚠ THE 65% IS THE WHOLE TRADE-OFF, AND IT IS WHY ARC-IC USUALLY LOSES.
// ARC-IC pays on 65% of base acres; ARC-CO and PLC pay on 85%. So ARC-IC needs a per-acre
// payment 85/65 = 1.3077x higher just to break even — on a $40/ac ARC-CO expectation it
// must beat about $52. In a year when the whole county is short, ARC-CO wins; ARC-IC earns
// its place when the FARM fell much harder than the county did.
export const ARC_IC_BASE_ACRE_FACTOR = 0.65;
// ⚠ 85% IS NOT RE-TYPED HERE. `arcPlcRule.BASE_ACRE_FACTOR` is the same statutory number the
// ARC-CO and PLC engines already pay on, and two constants for one figure is exactly how the
// pair drifts — this file records three copies of `normFarmerName` and two guarantee engines
// that disagreed about one farm's forward-sell ceiling. The break-even multiple below is
// DERIVED from both, so it cannot be left stale by a change to either.
export { BASE_ACRE_FACTOR as ARC_CO_BASE_ACRE_FACTOR } from "./arcPlcRule.js";
import { BASE_ACRE_FACTOR as CO_FACTOR } from "./arcPlcRule.js";
export const ARC_IC_BREAKEVEN_MULTIPLE = CO_FACTOR / ARC_IC_BASE_ACRE_FACTOR;

// Applied to every farm-program payment. 5.7% for FY2026.
export const SEQUESTRATION_FRAC = 0.057;

// ⚠ FIVE YEARS IN, THREE YEARS COUNTED. An Olympic average drops the single highest and the
// single lowest observation and means the rest. With fewer than five it is not an Olympic
// average at all, and substituting a plain mean would quietly change what the number IS.
export const OLYMPIC_YEARS = 5;

const num = (v) => (v == null || v === "" || !isFinite(Number(v)) ? null : Number(v));

/**
 * olympicAverage(yields) -> number | null
 *
 * Null below OLYMPIC_YEARS observations — never a plain mean of what is there. The caller
 * must say "not enough years" rather than print a different statistic under the same name.
 */
export function olympicAverage(yields) {
  const ys = (yields || []).map(num).filter((v) => v != null && v >= 0);
  if (ys.length < OLYMPIC_YEARS) return null;
  const s = [...ys].sort((a, b) => a - b);
  const kept = s.slice(1, -1);
  return kept.reduce((t, v) => t + v, 0) / kept.length;
}

/**
 * arcIcFarmRevenue({ crops }) -> { benchmarkPerAcre, actualPerAcre, plantedAcres, ... }
 *
 * ⚠⚠ ARC-IC IS WHOLE-FARM AND STATE-WIDE, AND THAT IS THE RULE A PORT OF THE ARC-CO ENGINE
 * GETS WRONG. Every covered crop planted on ALL of a producer's ARC-IC farms in ONE STATE is
 * pooled into a single revenue calculation, weighted by PLANTED acres. A good soybean crop
 * offsets a bad corn crop; the worst field on the farm is not the answer and neither is any
 * single crop's shortfall. ARC-CO, by contrast, decides per crop per county.
 *
 * Each entry: { crop, plantedAcres, benchmarkYield, actualYield, benchmarkPrice, myaPrice }.
 * A crop missing any of those is EXCLUDED and named — it cannot be assumed average, because
 * the pool is exactly what decides the payment.
 */
export function arcIcFarmRevenue({ crops = [] } = {}) {
  let benchAc = 0, actualAc = 0, acres = 0;
  const used = [], missing = [];
  for (const c of crops) {
    const ac = num(c && c.plantedAcres);
    const by = num(c && c.benchmarkYield), ay = num(c && c.actualYield);
    const bp = num(c && c.benchmarkPrice), mp = num(c && c.myaPrice);
    const why = ac == null || ac <= 0 ? "no planted acres"
      : by == null ? "no benchmark yield (needs 5 years of the farm's own production)"
      : ay == null ? "no program-year yield reported"
      : bp == null ? "no ARC benchmark price for this crop"
      : mp == null ? "no MYA price for this crop" : null;
    if (why) { missing.push({ crop: c && c.crop, why }); continue; }
    benchAc += by * bp * ac;
    actualAc += ay * mp * ac;
    acres += ac;
    used.push({ crop: c.crop, plantedAcres: ac, benchmarkRevenue: by * bp, actualRevenue: ay * mp });
  }
  if (acres <= 0) return { ok: false, why: "no crop in this pool carries all of its inputs", missing, used };
  return {
    ok: true,
    plantedAcres: acres,
    benchmarkPerAcre: benchAc / acres,
    actualPerAcre: actualAc / acres,
    used, missing,
  };
}

/**
 * arcIcPayment({ benchmarkPerAcre, actualPerAcre, baseAcres }) -> the per-acre rate and the
 * farm payment, or a refusal.
 *
 * ⚠ THE CAP AND THE GUARANTEE ARE BOTH FRACTIONS OF THE BENCHMARK, NOT OF THE SHORTFALL.
 * payment rate = min( max(0, 0.90 x benchmark - actual), 0.12 x benchmark )
 */
export function arcIcPayment({ benchmarkPerAcre, actualPerAcre, baseAcres, capFrac = 0.12 } = {}) {
  const b = num(benchmarkPerAcre), a = num(actualPerAcre), ba = num(baseAcres);
  if (b == null || b <= 0 || a == null) return { ok: false, why: "revenue not established" };
  // ⚠ BASE ACRES ARE NOT INSURED ACRES AND ARE NOT PLANTED ACRES. They are FSA's own figure
  // for the farm and nothing in this database holds them — they are TYPED. A screener that
  // substituted planted acres would be inventing the one number the payment scales with.
  if (ba == null || ba <= 0) return { ok: false, why: "base acres not entered" };
  const guarantee = ARC_IC_GUARANTEE_FRAC * b;
  const shortfall = Math.max(0, guarantee - a);
  const cap = capFrac * b;
  const rate = Math.min(shortfall, cap);
  const gross = rate * ARC_IC_BASE_ACRE_FACTOR * ba;
  return {
    ok: true,
    guaranteePerAcre: guarantee,
    shortfallPerAcre: shortfall,
    capPerAcre: cap,
    cappedOut: shortfall > cap,
    ratePerAcre: rate,
    paidAcres: ARC_IC_BASE_ACRE_FACTOR * ba,
    grossPayment: gross,
    netPayment: gross * (1 - SEQUESTRATION_FRAC),
    triggered: rate > 0,
  };
}

/**
 * The yield at which ARC-IC starts paying, and where it maxes out, as a SHARE of the farm's
 * own benchmark yield. Single-crop only — with a pool the question has no single answer.
 *
 *   starts  = 0.90 x benchmarkPrice / myaPrice
 *   maxes   = (0.90 - 0.12) x benchmarkPrice / myaPrice
 *
 * ⚠ THESE MOVE WITH THE FINAL MYA, so they are DERIVED here rather than typed. At the
 * September 2026 projections they come out at corn 94.3% / 81.7%, soybeans 91.3% / 79.1%,
 * wheat 98.2% / 85.1% — which is why wheat stands out: at $6.40 almost any shortfall from a
 * grower's own average triggers something. A lower final MYA raises both thresholds, so
 * falling prices make ARC-IC MORE likely to pay, not less.
 */
export function arcIcYieldThresholds({ benchmarkPrice, myaPrice, capFrac = 0.12 } = {}) {
  const bp = num(benchmarkPrice), mp = num(myaPrice);
  if (bp == null || mp == null || mp <= 0) return null;
  return {
    startsBelow: (ARC_IC_GUARANTEE_FRAC * bp) / mp,
    maxesAt: ((ARC_IC_GUARANTEE_FRAC - capFrac) * bp) / mp,
  };
}

/**
 * What ARC-CO would have to pay for ARC-IC to be the better election, and vice versa.
 * Pure arithmetic on the two base-acre factors — no judgement, no tie-break.
 */
export function arcIcVsCo({ arcIcRatePerAcre, arcCoRatePerAcre } = {}) {
  const ic = num(arcIcRatePerAcre), co = num(arcCoRatePerAcre);
  if (ic == null || co == null) return { ok: false, why: "both rates are needed to compare" };
  const icPaid = ic * ARC_IC_BASE_ACRE_FACTOR;
  const coPaid = co * CO_FACTOR;
  return {
    ok: true,
    icPerBaseAcre: icPaid,
    coPerBaseAcre: coPaid,
    // The ARC-IC rate that would match today's ARC-CO rate on the same base acres.
    icRateToMatchCo: co * ARC_IC_BREAKEVEN_MULTIPLE,
    better: icPaid > coPaid ? "ARC-IC" : coPaid > icPaid ? "ARC-CO" : "tie",
    differencePerBaseAcre: icPaid - coPaid,
  };
}
